import { defineSchema, entityDef, enumScalar, hasMany, hasOne, scalar } from '@contember/bindx'

export type CourseFormat = 'online' | 'inPerson' | 'hybrid' | 'eLearning'
export type CourseTermStatus = 'upcoming' | 'cancelled' | 'past'

export type OrderStatus = 'new' | 'awaitingPayment' | 'paid' | 'substitute' | 'cancelled'
export type OrderBillingType = 'individual' | 'company'
export type EmailLogType =
	| 'orderConfirmation'
	| 'substituteAck'
	| 'promotedFromSubstitute'
	| 'paymentReminder'
	| 'finalInvoice'
	| 'preCourseInfo'
	| 'gdMessage'
	| 'adminNotification'
	| 'contactForm'
	| 'courseInterest'
export type EmailLogStatus = 'pending' | 'sent' | 'queued' | 'rejected' | 'invalid' | 'failed'
export type ContactFormFieldType = 'text' | 'email' | 'textarea' | 'select' | 'radio' | 'checkbox'
export type ContactFormSubmissionStatus = 'received' | 'sent' | 'emailFailed'

export type LinkType = 'internal' | 'external'
export type ContentReferenceType = 'image' | 'gallery' | 'medallion' | 'tip' | 'youtube' | 'spotify' | 'cta' | 'article' | 'social'
export type ContentReferenceImageAlignment = 'left' | 'right'
export type ArticleTopic = 'aktualne' | 'fundraising' | 'komunikace' | 'inovace' | 'lide' | 'management'
/** `Article.secondaryTopics` is a list enum — a scalar for the same reason as `PersonRoleList` below. */
export type ArticleTopicList = string
export type PersonRole = 'expert' | 'author' | 'team' | 'partner' | 'lecturer' | 'coLecturer' | 'consultant' | 'employee' | 'staff'
/**
 * `Person.roles` is a list enum column, and bindx has no key mapping for one:
 * `ScalarKeys` drops every array field and `HasManyKeys` takes only arrays of
 * entities, so a `PersonRole[]` declaration would make the field disappear from
 * the entity's refs. Declared as a scalar — the accessor's value really is
 * `PersonRole[] | null`, which the roles field casts on read and on write.
 */
export type PersonRoleList = string
export type PersonUrlFamily = 'lideSvetaNeziskovek' | 'autori' | 'tymSvetaNeziskovek' | 'partaciSvetaNeziskovek'

export type PageTemplate =
	| 'default'
	| 'landing'
	| 'prose'
	| 'proseAside'
	| 'homepage'
	| 'kontakty'
	| 'poradenstvi'
	| 'eLearningy'
	| 'financovani'
	| 'burzaPrace'
	| 'ngoExcellence'
export type BlockType =
	| 'heroIntro'
	| 'heroBanner'
	| 'pageHeader'
	| 'richText'
	| 'articleGrid'
	| 'articleSpotlight'
	| 'courseList'
	| 'podcastList'
	| 'peopleGrid'
	| 'testimonials'
	| 'featureList'
	| 'linkCards'
	| 'cta'
	| 'newsletter'
	| 'contactForm'
	| 'partnerLogos'
	| 'embed'
	| 'spacer'
	| 'divider'
	| 'table'
	| 'gdHero'
	| 'gdStats'
	| 'gdCallsBand'
	| 'gdSteps'
	| 'gdTestimonials'
	| 'gdBand'
	| 'gdTeam'
	| 'gdFeatures'
	| 'gdPricing'
	| 'gdPageHeader'
	| 'gdContactWays'
	| 'gdContactForm'
	| 'gdDefinitionList'
	| 'gdLinkCard'
	| 'gdText'
	| 'gdOrderForm'
	| 'gdOrderThanks'
	| 'gdDemoForm'
	| 'gdDemoConfirmation'
	| 'gdCallSubmitForm'
	| 'gdLoginForm'
/** One grab-bag for every type; which values a block accepts is its form's rule. */
export type BlockVariant =
	| 'grid'
	| 'list'
	| 'featured'
	| 'carousel'
	| 'tiles'
	| 'consultants'
	| 'orange'
	| 'navy'
	| 'youtube'
	| 'spotify'
	| 'small'
	| 'large'
	| 'upcoming'
/** Tón pozadí sekce; prázdno = bílá. */
export type BlockBackground = 'white' | 'muted'
/** Per-item accent; null inherits the block type's default. */
export type BlockItemTone = 'orange' | 'green' | 'navy'

export type SupportCtaKey = 'spoluprace' | 'pravniKonzultace' | 'konzultaceProjektovychZadosti'

export type GdCallStatus = 'draft' | 'pendingApproval' | 'published'
export type GdOrderStatus = 'new' | 'awaitingPayment' | 'paid' | 'cancelled' | 'expired'
export type GdDiscountCodeOrigin = 'manual' | 'reactivation'
export type GdTaskType =
	| 'phoneNewOrderDue'
	| 'phoneNoLogin'
	| 'phoneRenewalDue'
	| 'phoneFarewell'
	| 'phoneReactivation'
	| 'contactChangeReview'
	| 'other'

// JSON-column payload. Must be a union that includes primitives so bindx
// classifies it as a scalar field — a bare `unknown`/object type is treated
// as a has-one relation by `ScalarKeys`/`HasOneKeys` (see admin CLAUDE.md).
export type JsonValue = string | number | boolean | null | { [key: string]: JsonValue } | JsonValue[]

// Scalar fields use their non-null payload type even when the underlying
// Contember column is nullable: in bindx every field value is `T | null` at
// the accessor level (`FieldAccessor.value`), and `FieldRef<T>` is invariant
// in `T`, so the non-null payload is the canonical modelling. Components like
// `CheckboxField` (which require `FieldRef<boolean>`) depend on this. Relations
// keep their `| null` — has-one nullability is meaningful for connect/disconnect
// and is stripped via `NonNullable` when mapped to a `HasOneRef`.
// ── CMS foundations (F0) ───────────────────────────────────────────────────
// `Image`, `Content` and `Link` shadow DOM globals of the same name. That is
// harmless for type positions, but a module that also needs `new Image()` or
// the `Link` DOM type must alias on import.

export interface Image {
	id: string
	url: string
	alt: string
	width: number
	height: number
	caption: string
	focalPointX: number
	focalPointY: number
}

export interface Url {
	id: string
	url: string
	label: string
	isSystemRoute: boolean
	courseLink: Course | null
	participants: Participant[]
	articleLink: Article | null
	personLink: Person | null
	podcastLink: Podcast | null
	pageLink: Page | null
}

export interface Link {
	id: string
	type: LinkType
	title: string
	internalTarget: Url | null
	externalTarget: string
}

export interface SeoMeta {
	id: string
	title: string
	description: string
	image: Image | null
	noIndex: boolean
	noFollow: boolean
	courseSeo: Course | null
	articleSeo: Article | null
	podcastSeo: Podcast | null
	pageSeo: Page | null
}

export interface Content {
	id: string
	data: JsonValue
	references: ContentReference[]
	// Inverses the ACL predicates read; no admin form uses them.
	articleContent: Article | null
	podcastContent: Podcast | null
	personBio: Person | null
	nestedOfReference: ContentReference | null
	blockContent: Block | null
	blockItemContent: BlockItem | null
	footerBottomText: Footer | null
	supportCtaText: SupportCta | null
}

export interface ContentReference {
	id: string
	content: Content
	type: ContentReferenceType
	title: string
	text: string
	image: Image | null
	link: Link | null
	person: Person | null
	/** `article`: the article whose card the box draws. */
	article: Article | null
	embedUrl: string
	galleryItems: ContentGalleryItem[]
	nested: Content | null
	/** `tip`: which side its `image` sits on. Null is left. */
	imageAlignment: ContentReferenceImageAlignment | null
}

export interface ContentGalleryItem {
	id: string
	reference: ContentReference
	image: Image
	order: number
}

export interface Redirect {
	id: string
	fromPath: string
	toUrl: string
	status: number
	active: boolean
	note: string | null
}

// ── CMS content (F1) ───────────────────────────────────────────────────────

export interface Banner {
	id: string
	name: string
	url: string
	openInNewTab: boolean
	publishedFrom: string
	publishedTo: string
	image: Image
}

export interface Article {
	id: string
	title: string
	perex: string
	ogDescription: string
	topic: ArticleTopic
	secondaryTopics: ArticleTopicList
	heroImage: Image | null
	author: Person | null
	authorName: string
	authorAffiliation: string
	authorPhoto: string
	tags: Tag[]
	content: Content | null
	url: Url | null
	seo: SeoMeta | null
	blocksAfter: BlockList | null
	relatedArticles: ArticleRelatedArticle[]
	relatedCourses: ArticleRelatedCourse[]
	publishedAt: string
	isFeatured: boolean
	publishState: ArticlePublishState | null
}

/** Curated picks for the related strip; `order` is what a plain many-to-many could not keep. */
export interface ArticleRelatedArticle {
	id: string
	article: Article
	related: Article
	order: number
}

export interface ArticleRelatedCourse {
	id: string
	article: Article
	course: Course
	order: number
}

/** Read-only SQL view: `publishedAt` resolved against `now()`. */
export interface ArticlePublishState {
	id: string
	article: Article
	isPublished: boolean
}

export interface Person {
	id: string
	name: string
	academicTitle: string
	roles: PersonRoleList
	urlFamily: PersonUrlFamily
	affiliation: string
	jobTitle: string
	email: string
	phone: string
	photo: Image | null
	bio: Content | null
	ogDescription: string
	showOnHomepage: boolean
	orderHomepage: number
	showInTeam: boolean
	orderTeam: number
	showInContacts: boolean
	orderContacts: number
	showAsConsultant: boolean
	orderConsultants: number
	consultantSpec: string
	consultantDescription: string
	showAsMentor: boolean
	orderMentors: number
	orderAuthors: number
	order: number
	isPriority: boolean
	url: Url | null
	blocksAfter: BlockList | null
	articles: Article[]
	podcasts: Podcast[]
	coursesAsLecturer: Course[]
	coursesAsContactPerson: Course[]
}

export interface Tag {
	id: string
	title: string
	slug: string
	articles: Article[]
	podcasts: Podcast[]
}

export interface Podcast {
	id: string
	title: string
	perex: string
	ogDescription: string
	spotifyEmbedUrl: string
	heroImage: Image | null
	author: Person | null
	tags: Tag[]
	content: Content | null
	url: Url | null
	seo: SeoMeta | null
	blocksAfter: BlockList | null
	publishedAt: string
	publishState: PodcastPublishState | null
}

export interface PodcastPublishState {
	id: string
	podcast: Podcast
	isPublished: boolean
}

// ── CMS pages & blocks (F2) ────────────────────────────────────────────────

export interface Page {
	id: string
	title: string
	template: PageTemplate
	url: Url | null
	seo: SeoMeta | null
	blocks: BlockList | null
	publishedAt: string
	publishState: PagePublishState | null
}

/** Read-only SQL view: `publishedAt` resolved against `now()`. */
export interface PagePublishState {
	id: string
	page: Page
	isPublished: boolean
}

export interface BlockList {
	id: string
	items: Block[]
}

/**
 * One wide row per block; which slots carry meaning depends on `type`. The
 * catalogue lives in `packages/api/model/cms/block.ts`.
 */
export interface Block {
	id: string
	type: BlockType
	order: number
	list: BlockList
	title: string
	subtitle: string
	text: string
	content: Content | null
	image: Image | null
	backgroundImage: Image | null
	primaryLink: Link | null
	secondaryLink: Link | null
	variant: BlockVariant
	background: BlockBackground
	isDark: boolean
	itemsLimit: number
	filterTopic: ArticleTopic
	filterTag: Tag | null
	form: ContactForm | null
	embedUrl: string
	/** Short label (gd-web: highlighted price plan tag). */
	badge: string
	/** Small print under the block, Markdown (gd-web). */
	note: string
	/** `{ [slotKey]: override }` of the gd-web form blocks (`gdFormBlockSlots`); empty = every text default. */
	data: JsonValue
	items: BlockItem[]
	articles: BlockArticle[]
	courses: BlockCourse[]
	people: BlockPerson[]
}

export interface BlockItem {
	id: string
	block: Block
	order: number
	primaryText: string
	secondaryText: string
	tertiaryText: string
	/** Rich card body; plain `secondaryText` stays the fallback for items without one. */
	content: Content | null
	tone: BlockItemTone
	email: string
	phone: string
	icon: string
	date: string
	image: Image | null
	link: Link | null
	person: Person | null
}

export interface BlockArticle {
	id: string
	block: Block
	article: Article
	order: number
}

export interface BlockCourse {
	id: string
	block: Block
	course: Course
	order: number
}

export interface BlockPerson {
	id: string
	block: Block
	person: Person
	order: number
}

export interface Navigation {
	id: string
	unique: 'unique'
	items: NavigationItem[]
}

export interface NavigationItem {
	id: string
	navigation: Navigation | null
	label: string
	link: Link | null
	order: number
	parent: NavigationItem | null
	children: NavigationItem[]
}

export interface Footer {
	id: string
	unique: 'unique'
	columns: FooterColumn[]
	bottomText: Content | null
}

export interface FooterColumn {
	id: string
	footer: Footer
	title: string
	order: number
	link: Link | null
	links: FooterLink[]
}

export interface FooterLink {
	id: string
	column: FooterColumn
	label: string
	link: Link | null
	order: number
}

export interface SupportCta {
	id: string
	key: SupportCtaKey
	title: string
	text: Content | null
	person: Person | null
	contactRole: string
	contactEmail: string
	contactPhone: string
	buttonLabel: string
	button: Link | null
}

export interface SiteSettings {
	id: string
	unique: 'unique'
	siteName: string
	defaultShareImage: Image | null
	organizationLogo: Image | null
	socials: JsonValue
	newsletterTitle: string
	newsletterText: string
	newsletterBullets: JsonValue
	newsletterButtonLabel: string
}

export interface Course {
	id: string
	title: string
	slug: string
	perex: string
	description: string
	heroImage: string
	heroImageEntity: Image | null
	url: Url | null
	seo: SeoMeta | null
	blocksAfter: BlockList | null
	ogDescription: string
	format: CourseFormat
	priceKc: number
	duration: string
	defaultCapacity: number
	externalRegistrationUrl: string
	contactPersonName: string
	contactPersonEmail: string
	contactPersonPhone: string
	contactPersonPhoto: string
	contactPerson: Person | null
	lecturers: Person[]
	tags: CourseTag[]
	isPublished: boolean
	isFeatured: boolean
	terms: CourseTerm[]
	interests: CourseInterest[]
}

export interface CourseInterest {
	id: string
	course: Course
	email: string
	firstName: string
	lastName: string
	note: string
	createdAt: string
	notifiedAt: string
}

export interface CourseTag {
	id: string
	name: string
	slug: string
	color: string
	order: number
	courses: Course[]
}

export interface CourseTerm {
	id: string
	course: Course
	createdAt: string
	location: string
	mapEmbed: string
	priceKc: number
	capacity: number
	registeredCount: number
	status: CourseTermStatus
	heroImageEntity: Image | null
	preCourseInfoEmailEnabled: boolean
	preCourseInfoInstructions: string
	firstDate: string
	dates: CourseTermDate[]
}

export interface CourseTermDate {
	id: string
	term: CourseTerm
	startsAt: string
	endsAt: string
	location: string
	mapEmbed: string
}

export interface DiscountCode {
	id: string
	code: string
	percentOff: number
	maxUses: number
	usesCount: number
	validFrom: string
	validUntil: string
	appliesToCourses: Course[]
	note: string
}

export interface ContactForm {
	id: string
	name: string
	slug: string
	recipientEmail: string
	subjectPrefix: string
	isEnabled: boolean
	successMessage: string
	createdAt: string
	fields: ContactFormField[]
	submissions: ContactFormSubmission[]
}

export interface ContactFormField {
	id: string
	form: ContactForm
	key: string
	label: string
	type: ContactFormFieldType
	required: boolean
	options: JsonValue
	order: number
}

export interface ContactFormSubmission {
	emailLogs: EmailLog[]
	id: string
	form: ContactForm
	payloadJson: JsonValue
	contactEmail: string
	status: ContactFormSubmissionStatus
	emailError: string
	createdAt: string
	sentAt: string
}

export interface Order {
	id: string
	orderNumber: number
	status: OrderStatus
	isManual: boolean
	course: Course
	courseTerm: CourseTerm | null | null
	contactName: string
	contactEmail: string
	contactPhone: string
	billingType: OrderBillingType
	billingName: string
	billingIc: string
	billingDic: string
	billingStreet: string
	billingCity: string
	billingZip: string
	billingCountry: string
	organization: Organization | null
	pricePerParticipant: number
	participantsCount: number
	discountCode: DiscountCode | null
	discountPercent: number
	totalKc: number
	note: string
	consentTermsAt: string
	consentNewsletter: boolean
	fapiInvoiceId: string
	fapiInvoiceUrl: string
	fapiPaymentUrl: string
	fapiDueDate: string
	fapiPaidInvoiceId: string
	fapiPaidInvoiceUrl: string
	fapiPaidAt: string
	fapiPaymentNotifiedAt: string
	fapiFinalInvoiceCheckAttempts: number
	fapiFinalInvoiceCheckRetryAt: string
	fapiFinalInvoiceProblemAt: string
	fapiFinalInvoiceProblem: string
	confirmationSentAt: string
	reminderSentAt: string
	finalInvoiceSentAt: string
	preCourseInfoSentAt: string
	participants: Participant[]
	emailLogs: EmailLog[]
	termChanges: OrderTermChange[]
	createdAt: string
}

/** Audit stopa `order.changeTerm` — viz `OrderTermChange` v `packages/api/model/order.ts`. */
export interface OrderTermChange {
	id: string
	order: Order
	fromTerm: CourseTerm | null
	toTerm: CourseTerm | null
	participants: Participant[]
	participantsCount: number
	actorPersonId: string
	actorEmail: string
	createdAt: string
}

export interface EmailLog {
	contactFormSubmission: ContactFormSubmission | null
	id: string
	/** Null for GD mail, which hangs off the `gd*` relations instead. */
	order: Order | null
	gdOrder: GdOrder | null
	gdUser: GdUser | null
	gdDemoUser: GdDemoUser | null
	templateKey: string
	type: EmailLogType
	status: EmailLogStatus
	subject: string
	fromEmail: string
	fromName: string
	replyTo: string
	recipientEmail: string
	recipientName: string
	recipientsJson: JsonValue
	tagsJson: JsonValue
	attachmentsJson: JsonValue
	providerMessageId: string
	providerStatus: string
	providerRejectReason: string
	errorMessage: string
	html: string
	text: string
	contentExpiresAt: string
	contentPurgedAt: string
	createdAt: string
	sentAt: string
}

export interface EmailText {
	id: string
	key: string
	group: string
	label: string
	value: string
	variables: string
	order: number
}

export interface Organization {
	id: string
	ic: string
	dic: string
	name: string
	street: string
	city: string
	zip: string
	country: string
	note: string
	orders: Order[]
	firstOrderAt: string
	lastOrderAt: string
}

export interface Participant {
	id: string
	order: Order
	courseTerm: CourseTerm | null
	preCourseInfoSentAt: string | null
	firstName: string
	lastName: string
	email: string
	phone: string
	isContactPerson: boolean
	attended: boolean
}

export interface User {
	id: string
	personId: string
	email: string
	firstName: string
	lastName: string
	createdAt: string
}

// Grantový diář. Credential material is deliberately absent so it can never
// reach the admin client: GdUser omits the password and reset-token columns,
// GdDemoUser omits its confirm-token columns, and GdSession is not modelled at
// all. GdScheduledJob has no UI.
export interface GdTargetGroup {
	id: string
	name: string
	order: number
	legacyId: number
	calls: GdCall[]
}

export interface GdFocusArea {
	id: string
	name: string
	order: number
	legacyId: number
	calls: GdCall[]
}

export interface GdRegion {
	id: string
	name: string
	order: number
	legacyId: number
	calls: GdCall[]
}

export interface GdIssuerGroup {
	id: string
	name: string
	order: number
	legacyId: number
	issuers: GdIssuer[]
}

export interface GdPosition {
	id: string
	name: string
	order: number
	legacyId: number
	contacts: GdIssuerContact[]
}

export interface GdLegalForm {
	id: string
	name: string
	code: string
	order: number
	issuers: GdIssuer[]
}

export interface GdIssuer {
	id: string
	name: string
	ico: string
	dic: string
	icDphSk: string
	email: string
	phone: string
	web: string
	logo: string
	note: string
	group: GdIssuerGroup | null
	legalFormRef: GdLegalForm | null
	legalForm: string
	addresses: GdIssuerAddress[]
	defaultAddress: GdIssuerAddress | null
	contacts: GdIssuerContact[]
	calls: GdCall[]
	createdAt: string
	legacyCustomerId: number
}

export interface GdIssuerAddress {
	id: string
	issuer: GdIssuer
	label: string
	street: string
	city: string
	zip: string
	country: string
	legacyId: number
	calls: GdCall[]
}

export interface GdIssuerContact {
	id: string
	issuer: GdIssuer
	titles: string
	firstName: string
	lastName: string
	email: string
	phone: string
	position: GdPosition | null
	isActive: boolean
	legacyId: number
	calls: GdCall[]
}

export interface GdCall {
	id: string
	title: string
	publicTitle: string
	status: GdCallStatus
	isPublic: boolean
	issuer: GdIssuer | null
	issuerAddress: GdIssuerAddress | null
	lastUpdatedAt: string
	perex: string
	/** Legacy HTML kept as provenance; the canonical text is `descriptionRich`. */
	description: string
	descriptionRich: JsonValue
	internalNote: string
	websiteUrl: string
	targetGroups: GdTargetGroup[]
	focusAreas: GdFocusArea[]
	regions: GdRegion[]
	contacts: GdIssuerContact[]
	maxDeadlineAt: string
	deadlines: GdCallDeadline[]
	seminars: GdCallSeminar[]
	notifications: GdCallNotification[]
	favorites: GdFavoriteCall[]
	favoriteCount: number
	publishedAt: string
	suppressNotification: boolean
	submittedIssuerText: string
	submittedContactText: string
	submittedSeminarText: string
	offersConsent: boolean
	offersEmail: string
	submissionNotificationDueAt: string
	createdAt: string
	legacyId: number
}

export interface GdCallDeadline {
	id: string
	call: GdCall
	date: string
	isRolling: boolean
	allocation: string
	grants: string
	info: string
	order: number
	legacyId: number
}

export interface GdCallSeminar {
	id: string
	call: GdCall
	title: string
	dateText: string
	place: string
	info: string
	contact: string
	order: number
	legacyId: number
}

export interface GdCallNotification {
	id: string
	call: GdCall
	notifyAt: string
	notifiedAt: string
	order: number
	createdAt: string
}

export interface GdSubscriber {
	id: string
	name: string
	isPerson: boolean
	ico: string
	dic: string
	icDphSk: string
	email: string
	phone: string
	web: string
	note: string
	billingStreet: string
	billingCity: string
	billingZip: string
	billingCountry: string
	dueDays: number
	standingDiscountPercent: number
	autoRenewDisabled: boolean
	autoRenewDisabledAt: string
	memberships: GdMembership[]
	orders: GdOrder[]
	createdAt: string
	legacyCustomerId: number
}

export interface GdMembership {
	id: string
	user: GdUser
	subscriber: GdSubscriber
	isBillingContact: boolean
	isActive: boolean
	createdAt: string
}

export interface GdUser {
	id: string
	email: string
	titles: string
	firstName: string
	lastName: string
	salutation: string
	phone: string
	organizationText: string
	isActive: boolean
	consentNewsletter: boolean
	consentNewsletterAt: string
	consentAge16: boolean
	consentAge16At: string
	consentMarketing: boolean
	consentMarketingAt: string
	registeredAt: string
	firstLoginAt: string
	lastLoginAt: string
	lastDigestAt: string
	notificationsEnabled: boolean
	digestIntervalDays: number
	filterTargetGroups: GdTargetGroup[]
	filterFocusAreas: GdFocusArea[]
	filterRegions: GdRegion[]
	filterIssuerGroups: GdIssuerGroup[]
	favoriteCalls: GdFavoriteCall[]
	favoriteIssuers: GdIssuer[]
	memberships: GdMembership[]
	legacyUserId: number
}

export interface GdFavoriteCall {
	id: string
	user: GdUser
	call: GdCall
	createdAt: string
}

export type GdChromeLinkPlacement = 'topbar' | 'menu' | 'menuUser' | 'footer'

/** Header, menu and footer copy of gd-web; a singleton like `GdSettings`. Empty string columns fall back to `gdDefaultChrome`. */
export interface GdChrome {
	id: string
	unique: 'unique'
	/** One list for every placement, ordered by `order` within a placement. */
	links: GdChromeLink[]
	demoButtonLabel: string
	orderButtonLabel: string
	logoutLabel: string
	demoBadgeLabel: string
	callsLabel: string
	publicCallsLabel: string
	footerBrand: string
	/** Markdown. */
	footerClaim: string
	footerContactHeading: string
	footerLinksHeading: string
	legalPrivacyLabel: string
	legalCookiesLabel: string
	legalSnLabel: string
}

export interface GdChromeLink {
	id: string
	chrome: GdChrome | null
	placement: GdChromeLinkPlacement
	order: number
	label: string
	/** Relative (`/objednavka`, `/#cenik`) or absolute URL. */
	href: string
}

/** gd-web marketing page built from blocks; see docs/grant/web-pages.md. */
export interface GdPage {
	id: string
	title: string
	/** `/` or `/slug`; application routes are reserved (`isReservedGdPath`). */
	path: string
	seoTitle: string
	seoDescription: string
	blocks: BlockList | null
	/** Null = draft, future = scheduled. */
	publishedAt: string
}

export interface GdTestimonial {
	id: string
	quote: string
	author: string
	role: string
	photo: Image | null
	isPublished: boolean
	order: number
	createdAt: string
}

export interface GdPlan {
	id: string
	name: string
	months: number
	price: number
	priceVat: number
	vatRate: number
	isActive: boolean
	order: number
	legacyId: number
	orders: GdOrder[]
}

export interface GdDiscountCode {
	id: string
	code: string
	percentOff: number
	maxUses: number
	usesCount: number
	validFrom: string
	validUntil: string
	plan: GdPlan | null
	subscriber: GdSubscriber | null
	origin: GdDiscountCodeOrigin
	note: string
	createdAt: string
	orders: GdOrder[]
}

export interface GdOrder {
	id: string
	orderNumber: number
	status: GdOrderStatus
	subscriber: GdSubscriber
	orderedByUser: GdUser | null
	plan: GdPlan | null
	price: number
	priceVat: number
	vatRate: number
	discountCode: GdDiscountCode | null
	appliedDiscountPercent: number
	periodStart: string
	periodEnd: string
	renewedFromOrder: GdOrder | null
	renewals: GdOrder[]
	fapiProformaId: string
	fapiProformaUrl: string
	fapiPaymentUrl: string
	fapiInvoiceId: string
	dueDate: string
	confirmationSentAt: string
	confirmationClaimedAt: string
	welcomeSentAt: string
	welcomeClaimedAt: string
	fapiProformaRequestedAt: string
	fapiProformaAttempts: number
	fapiProformaProblemAt: string
	fapiProformaProblem: string
	fapiPaymentCheckedAt: string
	paymentSettledVia: string
	processingAttempts: number
	processingRetryAt: string
	billingName: string
	billingIc: string
	billingDic: string
	billingStreet: string
	billingCity: string
	billingZip: string
	billingCountry: string
	billingEmail: string
	isManual: boolean
	paidOutsideFapi: boolean
	createdAt: string
	paidAt: string
	legacyId: number
}

/** Audit trail of the checkout answering "this IČO is already a customer" (spec.md §4a). */
export interface GdCheckoutProbe {
	id: string
	at: string
	ico: string
	outcome: string
	ip: string
}

export interface GdTask {
	id: string
	type: GdTaskType
	subscriber: GdSubscriber | null
	user: GdUser | null
	order: GdOrder | null
	reason: string
	dueAt: string
	doneAt: string
	doneBy: User | null
	note: string
	createdAt: string
}

export interface GdDemoUser {
	id: string
	firstName: string
	lastName: string
	phone: string
	email: string
	organization: string
	emailConfirmedAt: string
	firstAccessAt: string
	lastAccessAt: string
	accessCount: number
	mailchimpSyncedAt: string
	convertedToSubscriber: GdSubscriber | null
	createdAt: string
	legacyUserId: number
}

export interface GdSettings {
	id: string
	unique: 'unique'
	publicCallsEnabled: boolean
	adminNotificationEmail: string
	callSubmissionNotificationEmail: string
	digestSendHour: number
	deadlineReminderDays: number
	renewalGenerationHour: number
	renewalLeadDays: number
	dueDaysDefault: number
	paymentReminderDays: number
	paymentReminderCallDays: number
	loginReminderDays: number
	loginReminderCallDays: number
	usageCheckMonths: number
	renewalReminderDays: number
	renewalReminderCallDays: number
	reactivationOfferDays: number
	reactivationReminderDays: number
	reactivationCallDays: number
	reactivationDiscountPercent: number
	reactivationDiscountValidDays: number
	demoAccessReminderDays: number
	demoFirstImpressionsDays: number
}

export const adminSchema = defineSchema<{
	Article: Article
	ArticlePublishState: ArticlePublishState
	Person: Person
	Podcast: Podcast
	PodcastPublishState: PodcastPublishState
	Tag: Tag
	Page: Page
	PagePublishState: PagePublishState
	BlockList: BlockList
	Block: Block
	BlockItem: BlockItem
	ArticleRelatedArticle: ArticleRelatedArticle
	ArticleRelatedCourse: ArticleRelatedCourse
	BlockArticle: BlockArticle
	BlockCourse: BlockCourse
	BlockPerson: BlockPerson
	Navigation: Navigation
	NavigationItem: NavigationItem
	Footer: Footer
	FooterColumn: FooterColumn
	FooterLink: FooterLink
	SupportCta: SupportCta
	SiteSettings: SiteSettings
	ContactForm: ContactForm
	ContactFormField: ContactFormField
	ContactFormSubmission: ContactFormSubmission
	Content: Content
	ContentGalleryItem: ContentGalleryItem
	ContentReference: ContentReference
	Course: Course
	CourseInterest: CourseInterest
	CourseTag: CourseTag
	CourseTerm: CourseTerm
	CourseTermDate: CourseTermDate
	DiscountCode: DiscountCode
	Image: Image
	Link: Link
	Redirect: Redirect
	Banner: Banner
	SeoMeta: SeoMeta
	Url: Url
	EmailLog: EmailLog
	EmailText: EmailText
	GdCall: GdCall
	GdCallDeadline: GdCallDeadline
	GdCallNotification: GdCallNotification
	GdCallSeminar: GdCallSeminar
	GdDemoUser: GdDemoUser
	GdDiscountCode: GdDiscountCode
	GdFavoriteCall: GdFavoriteCall
	GdTestimonial: GdTestimonial
	GdChrome: GdChrome
	GdChromeLink: GdChromeLink
	GdPage: GdPage
	GdFocusArea: GdFocusArea
	GdIssuer: GdIssuer
	GdIssuerAddress: GdIssuerAddress
	GdIssuerContact: GdIssuerContact
	GdIssuerGroup: GdIssuerGroup
	GdLegalForm: GdLegalForm
	GdMembership: GdMembership
	GdOrder: GdOrder
	GdPlan: GdPlan
	GdPosition: GdPosition
	GdRegion: GdRegion
	GdSettings: GdSettings
	GdSubscriber: GdSubscriber
	GdTargetGroup: GdTargetGroup
	GdCheckoutProbe: GdCheckoutProbe
	GdTask: GdTask
	GdUser: GdUser
	Order: Order
	OrderTermChange: OrderTermChange
	Organization: Organization
	Participant: Participant
	User: User
}>({
	entities: {
		ContactForm: {
			fields: {
				id: scalar(),
				name: scalar(),
				slug: scalar(),
				recipientEmail: scalar(),
				subjectPrefix: scalar(),
				isEnabled: scalar(),
				successMessage: scalar(),
				createdAt: scalar(),
				fields: hasMany('ContactFormField', { inverse: 'form', relationKind: 'oneHasMany', nullable: false }),
				submissions: hasMany('ContactFormSubmission', { inverse: 'form', relationKind: 'oneHasMany', nullable: false }),
			},
		},
		ContactFormField: {
			fields: {
				id: scalar(),
				form: hasOne('ContactForm', { inverse: 'fields', nullable: false }),
				key: scalar(),
				label: scalar(),
				type: enumScalar('ContactFormFieldType', ['text', 'email', 'textarea', 'select', 'radio', 'checkbox']),
				required: scalar(),
				options: scalar(),
				order: scalar(),
			},
		},
		ContactFormSubmission: {
			fields: {
				emailLogs: hasMany('EmailLog', { inverse: 'contactFormSubmission', relationKind: 'oneHasMany', nullable: false }),
				id: scalar(),
				form: hasOne('ContactForm', { inverse: 'submissions', nullable: false }),
				payloadJson: scalar(),
				contactEmail: scalar(),
				status: enumScalar('ContactFormSubmissionStatus', ['received', 'sent', 'emailFailed']),
				emailError: scalar(),
				createdAt: scalar(),
				sentAt: scalar(),
			},
		},
		Image: {
			fields: {
				id: scalar(),
				url: scalar(),
				alt: scalar(),
				width: scalar(),
				height: scalar(),
				caption: scalar(),
				focalPointX: scalar(),
				focalPointY: scalar(),
			},
		},
		Url: {
			fields: {
				id: scalar(),
				url: scalar(),
				label: scalar(),
				isSystemRoute: scalar(),
				courseLink: hasOne('Course', { inverse: 'url' }),
				articleLink: hasOne('Article', { inverse: 'url' }),
				personLink: hasOne('Person', { inverse: 'url' }),
				podcastLink: hasOne('Podcast', { inverse: 'url' }),
				pageLink: hasOne('Page', { inverse: 'url' }),
			},
		},
		Link: {
			fields: {
				id: scalar(),
				type: enumScalar('LinkType', ['internal', 'external']),
				title: scalar(),
				internalTarget: hasOne('Url'),
				externalTarget: scalar(),
			},
		},
		SeoMeta: {
			fields: {
				id: scalar(),
				title: scalar(),
				description: scalar(),
				image: hasOne('Image'),
				noIndex: scalar(),
				noFollow: scalar(),
				courseSeo: hasOne('Course', { inverse: 'seo' }),
				articleSeo: hasOne('Article', { inverse: 'seo' }),
				podcastSeo: hasOne('Podcast', { inverse: 'seo' }),
				pageSeo: hasOne('Page', { inverse: 'seo' }),
			},
		},
		Content: {
			fields: {
				id: scalar(),
				data: scalar(),
				references: hasMany('ContentReference', { inverse: 'content', relationKind: 'oneHasMany', nullable: false }),
				articleContent: hasOne('Article', { inverse: 'content' }),
				podcastContent: hasOne('Podcast', { inverse: 'content' }),
				personBio: hasOne('Person', { inverse: 'bio' }),
				nestedOfReference: hasOne('ContentReference', { inverse: 'nested' }),
				blockContent: hasOne('Block', { inverse: 'content' }),
				blockItemContent: hasOne('BlockItem', { inverse: 'content' }),
				footerBottomText: hasOne('Footer', { inverse: 'bottomText' }),
				supportCtaText: hasOne('SupportCta', { inverse: 'text' }),
			},
		},
		ContentReference: {
			fields: {
				id: scalar(),
				content: hasOne('Content', { inverse: 'references', nullable: false }),
				type: enumScalar('ContentReferenceType', [
					'image',
					'gallery',
					'medallion',
					'tip',
					'youtube',
					'spotify',
					'cta',
					'article',
					'social',
				]),
				title: scalar(),
				text: scalar(),
				image: hasOne('Image'),
				link: hasOne('Link'),
				person: hasOne('Person'),
				article: hasOne('Article'),
				embedUrl: scalar(),
				imageAlignment: enumScalar('ContentReferenceImageAlignment', ['left', 'right']),
				galleryItems: hasMany('ContentGalleryItem', {
					inverse: 'reference',
					relationKind: 'oneHasMany',
					nullable: false,
				}),
				nested: hasOne('Content'),
			},
		},
		ContentGalleryItem: {
			fields: {
				id: scalar(),
				reference: hasOne('ContentReference', { inverse: 'galleryItems', nullable: false }),
				image: hasOne('Image', { nullable: false }),
				order: scalar(),
			},
		},
		Redirect: {
			fields: {
				id: scalar(),
				fromPath: scalar(),
				toUrl: scalar(),
				status: scalar(),
				active: scalar(),
				note: scalar(),
			},
		},
		Banner: {
			fields: {
				id: scalar(),
				name: scalar(),
				url: scalar(),
				openInNewTab: scalar(),
				publishedFrom: scalar(),
				publishedTo: scalar(),
				image: hasOne('Image'),
			},
		},
		Article: {
			fields: {
				id: scalar(),
				title: scalar(),
				perex: scalar(),
				ogDescription: scalar(),
				topic: enumScalar('ArticleTopic', ['aktualne', 'fundraising', 'komunikace', 'inovace', 'lide', 'management']),
				secondaryTopics: scalar(),
				heroImage: hasOne('Image'),
				author: hasOne('Person', { inverse: 'articles' }),
				authorName: scalar(),
				authorAffiliation: scalar(),
				authorPhoto: scalar(),
				tags: hasMany('Tag', { inverse: 'articles', relationKind: 'manyHasMany' }),
				content: hasOne('Content', { inverse: 'articleContent' }),
				url: hasOne('Url', { inverse: 'articleLink' }),
				seo: hasOne('SeoMeta', { inverse: 'articleSeo' }),
				blocksAfter: hasOne('BlockList'),
				relatedArticles: hasMany('ArticleRelatedArticle', { inverse: 'article', relationKind: 'oneHasMany', nullable: false }),
				relatedCourses: hasMany('ArticleRelatedCourse', { inverse: 'article', relationKind: 'oneHasMany', nullable: false }),
				publishedAt: scalar(),
				isFeatured: scalar(),
				publishState: hasOne('ArticlePublishState', { inverse: 'article' }),
			},
		},
		ArticlePublishState: {
			fields: {
				id: scalar(),
				article: hasOne('Article', { inverse: 'publishState', nullable: false }),
				isPublished: scalar(),
			},
		},
		Person: {
			fields: {
				id: scalar(),
				name: scalar(),
				academicTitle: scalar(),
				roles: scalar(),
				urlFamily: enumScalar('PersonUrlFamily', ['lideSvetaNeziskovek', 'autori', 'tymSvetaNeziskovek', 'partaciSvetaNeziskovek']),
				affiliation: scalar(),
				jobTitle: scalar(),
				email: scalar(),
				phone: scalar(),
				photo: hasOne('Image'),
				bio: hasOne('Content'),
				ogDescription: scalar(),
				showOnHomepage: scalar(),
				orderHomepage: scalar(),
				showInTeam: scalar(),
				orderTeam: scalar(),
				showInContacts: scalar(),
				orderContacts: scalar(),
				showAsConsultant: scalar(),
				orderConsultants: scalar(),
				consultantSpec: scalar(),
				consultantDescription: scalar(),
				showAsMentor: scalar(),
				orderMentors: scalar(),
				orderAuthors: scalar(),
				order: scalar(),
				isPriority: scalar(),
				url: hasOne('Url', { inverse: 'personLink' }),
				blocksAfter: hasOne('BlockList'),
				articles: hasMany('Article', { inverse: 'author', relationKind: 'oneHasMany' }),
				podcasts: hasMany('Podcast', { inverse: 'author', relationKind: 'oneHasMany' }),
				coursesAsLecturer: hasMany('Course', { inverse: 'lecturers', relationKind: 'manyHasMany' }),
				coursesAsContactPerson: hasMany('Course', { inverse: 'contactPerson', relationKind: 'oneHasMany' }),
			},
		},
		Tag: {
			fields: {
				id: scalar(),
				title: scalar(),
				slug: scalar(),
				articles: hasMany('Article', { inverse: 'tags', relationKind: 'manyHasMany' }),
				podcasts: hasMany('Podcast', { inverse: 'tags', relationKind: 'manyHasMany' }),
			},
		},
		Podcast: {
			fields: {
				id: scalar(),
				title: scalar(),
				perex: scalar(),
				ogDescription: scalar(),
				spotifyEmbedUrl: scalar(),
				heroImage: hasOne('Image'),
				author: hasOne('Person', { inverse: 'podcasts' }),
				tags: hasMany('Tag', { inverse: 'podcasts', relationKind: 'manyHasMany' }),
				content: hasOne('Content', { inverse: 'podcastContent' }),
				url: hasOne('Url', { inverse: 'podcastLink' }),
				seo: hasOne('SeoMeta', { inverse: 'podcastSeo' }),
				blocksAfter: hasOne('BlockList'),
				publishedAt: scalar(),
				publishState: hasOne('PodcastPublishState', { inverse: 'podcast' }),
			},
		},
		PodcastPublishState: {
			fields: {
				id: scalar(),
				podcast: hasOne('Podcast', { inverse: 'publishState', nullable: false }),
				isPublished: scalar(),
			},
		},
		Page: {
			fields: {
				id: scalar(),
				title: scalar(),
				template: enumScalar('PageTemplate', [
					'default',
					'landing',
					'prose',
					'proseAside',
					'homepage',
					'kontakty',
					'poradenstvi',
					'eLearningy',
					'financovani',
					'burzaPrace',
					'ngoExcellence',
				]),
				url: hasOne('Url', { inverse: 'pageLink' }),
				seo: hasOne('SeoMeta', { inverse: 'pageSeo' }),
				blocks: hasOne('BlockList'),
				publishedAt: scalar(),
				publishState: hasOne('PagePublishState', { inverse: 'page' }),
			},
		},
		PagePublishState: {
			fields: {
				id: scalar(),
				page: hasOne('Page', { inverse: 'publishState', nullable: false }),
				isPublished: scalar(),
			},
		},
		BlockList: {
			fields: {
				id: scalar(),
				items: hasMany('Block', { inverse: 'list', relationKind: 'oneHasMany', nullable: false }),
			},
		},
		Block: {
			fields: {
				id: scalar(),
				type: enumScalar('BlockType', [
					'heroIntro',
					'heroBanner',
					'pageHeader',
					'richText',
					'articleGrid',
					'articleSpotlight',
					'courseList',
					'podcastList',
					'peopleGrid',
					'testimonials',
					'featureList',
					'linkCards',
					'cta',
					'newsletter',
					'contactForm',
					'partnerLogos',
					'embed',
					'spacer',
					'divider',
					'table',
					'gdHero',
					'gdStats',
					'gdCallsBand',
					'gdSteps',
					'gdTestimonials',
					'gdBand',
					'gdTeam',
					'gdFeatures',
					'gdPricing',
					'gdPageHeader',
					'gdContactWays',
					'gdContactForm',
					'gdDefinitionList',
					'gdLinkCard',
					'gdText',
					'gdOrderForm',
					'gdOrderThanks',
					'gdDemoForm',
					'gdDemoConfirmation',
					'gdCallSubmitForm',
					'gdLoginForm',
				]),
				order: scalar(),
				list: hasOne('BlockList', { inverse: 'items', nullable: false }),
				title: scalar(),
				subtitle: scalar(),
				text: scalar(),
				content: hasOne('Content', { inverse: 'blockContent' }),
				image: hasOne('Image'),
				backgroundImage: hasOne('Image'),
				primaryLink: hasOne('Link'),
				secondaryLink: hasOne('Link'),
				variant: enumScalar('BlockVariant', [
					'grid',
					'list',
					'featured',
					'carousel',
					'tiles',
					'consultants',
					'orange',
					'navy',
					'youtube',
					'spotify',
					'small',
					'large',
					'upcoming',
				]),
				background: enumScalar('BlockBackground', ['white', 'muted']),
				isDark: scalar(),
				itemsLimit: scalar(),
				// Its own database enum, values kept identical to `ArticleTopic` — see
				// the note on `Block.filterTopic` in the model.
				filterTopic: enumScalar('BlockFilterTopic', [
					'aktualne',
					'fundraising',
					'komunikace',
					'inovace',
					'lide',
					'management',
				]),
				filterTag: hasOne('Tag'),
				form: hasOne('ContactForm'),
				embedUrl: scalar(),
				badge: scalar(),
				note: scalar(),
				data: scalar(),
				items: hasMany('BlockItem', { inverse: 'block', relationKind: 'oneHasMany', nullable: false }),
				articles: hasMany('BlockArticle', { inverse: 'block', relationKind: 'oneHasMany', nullable: false }),
				courses: hasMany('BlockCourse', { inverse: 'block', relationKind: 'oneHasMany', nullable: false }),
				people: hasMany('BlockPerson', { inverse: 'block', relationKind: 'oneHasMany', nullable: false }),
			},
		},
		BlockItem: {
			fields: {
				id: scalar(),
				block: hasOne('Block', { inverse: 'items', nullable: false }),
				order: scalar(),
				primaryText: scalar(),
				secondaryText: scalar(),
				tertiaryText: scalar(),
				content: hasOne('Content', { inverse: 'blockItemContent' }),
				tone: enumScalar('BlockItemTone', ['orange', 'green', 'navy']),
				email: scalar(),
				phone: scalar(),
				icon: scalar(),
				date: scalar(),
				image: hasOne('Image'),
				link: hasOne('Link'),
				person: hasOne('Person'),
			},
		},
		ArticleRelatedArticle: {
			fields: {
				id: scalar(),
				article: hasOne('Article', { inverse: 'relatedArticles', nullable: false }),
				related: hasOne('Article', { nullable: false }),
				order: scalar(),
			},
		},
		ArticleRelatedCourse: {
			fields: {
				id: scalar(),
				article: hasOne('Article', { inverse: 'relatedCourses', nullable: false }),
				course: hasOne('Course', { nullable: false }),
				order: scalar(),
			},
		},
		BlockArticle: {
			fields: {
				id: scalar(),
				block: hasOne('Block', { inverse: 'articles', nullable: false }),
				article: hasOne('Article', { nullable: false }),
				order: scalar(),
			},
		},
		BlockCourse: {
			fields: {
				id: scalar(),
				block: hasOne('Block', { inverse: 'courses', nullable: false }),
				course: hasOne('Course', { nullable: false }),
				order: scalar(),
			},
		},
		BlockPerson: {
			fields: {
				id: scalar(),
				block: hasOne('Block', { inverse: 'people', nullable: false }),
				person: hasOne('Person', { nullable: false }),
				order: scalar(),
			},
		},
		Navigation: {
			fields: {
				id: scalar(),
				unique: enumScalar('NavigationUnique', ['unique']),
				items: hasMany('NavigationItem', { inverse: 'navigation', relationKind: 'oneHasMany' }),
			},
		},
		NavigationItem: {
			fields: {
				id: scalar(),
				navigation: hasOne('Navigation', { inverse: 'items' }),
				label: scalar(),
				link: hasOne('Link'),
				order: scalar(),
				parent: hasOne('NavigationItem', { inverse: 'children' }),
				children: hasMany('NavigationItem', { inverse: 'parent', relationKind: 'oneHasMany' }),
			},
		},
		Footer: {
			fields: {
				id: scalar(),
				unique: enumScalar('FooterUnique', ['unique']),
				columns: hasMany('FooterColumn', { inverse: 'footer', relationKind: 'oneHasMany', nullable: false }),
				bottomText: hasOne('Content', { inverse: 'footerBottomText' }),
			},
		},
		FooterColumn: {
			fields: {
				id: scalar(),
				footer: hasOne('Footer', { inverse: 'columns', nullable: false }),
				title: scalar(),
				order: scalar(),
				link: hasOne('Link'),
				links: hasMany('FooterLink', { inverse: 'column', relationKind: 'oneHasMany', nullable: false }),
			},
		},
		FooterLink: {
			fields: {
				id: scalar(),
				column: hasOne('FooterColumn', { inverse: 'links', nullable: false }),
				label: scalar(),
				link: hasOne('Link'),
				order: scalar(),
			},
		},
		SupportCta: {
			fields: {
				id: scalar(),
				key: enumScalar('SupportCtaKey', ['spoluprace', 'pravniKonzultace', 'konzultaceProjektovychZadosti']),
				title: scalar(),
				text: hasOne('Content', { inverse: 'supportCtaText' }),
				person: hasOne('Person'),
				contactRole: scalar(),
				contactEmail: scalar(),
				contactPhone: scalar(),
				buttonLabel: scalar(),
				button: hasOne('Link'),
			},
		},
		SiteSettings: {
			fields: {
				id: scalar(),
				unique: enumScalar('SiteSettingsUnique', ['unique']),
				siteName: scalar(),
				defaultShareImage: hasOne('Image'),
				organizationLogo: hasOne('Image'),
				socials: scalar(),
				newsletterTitle: scalar(),
				newsletterText: scalar(),
				newsletterBullets: scalar(),
				newsletterButtonLabel: scalar(),
			},
		},
		Course: {
			fields: {
				id: scalar(),
				title: scalar(),
				slug: scalar(),
				perex: scalar(),
				description: scalar(),
				heroImage: scalar(),
				heroImageEntity: hasOne('Image'),
				url: hasOne('Url', { inverse: 'courseLink' }),
				seo: hasOne('SeoMeta', { inverse: 'courseSeo' }),
				blocksAfter: hasOne('BlockList'),
				ogDescription: scalar(),
				format: enumScalar('CourseFormat', ['online', 'inPerson', 'hybrid', 'eLearning']),
				priceKc: scalar(),
				duration: scalar(),
				defaultCapacity: scalar(),
				externalRegistrationUrl: scalar(),
				contactPersonName: scalar(),
				contactPersonEmail: scalar(),
				contactPersonPhone: scalar(),
				contactPersonPhoto: scalar(),
				contactPerson: hasOne('Person', { inverse: 'coursesAsContactPerson' }),
				lecturers: hasMany('Person', { inverse: 'coursesAsLecturer', relationKind: 'manyHasMany' }),
				tags: hasMany('CourseTag', { inverse: 'courses', relationKind: 'manyHasMany' }),
				isPublished: scalar(),
				isFeatured: scalar(),
				terms: hasMany('CourseTerm', { inverse: 'course', relationKind: 'oneHasMany', nullable: false }),
				interests: hasMany('CourseInterest', { inverse: 'course', relationKind: 'oneHasMany', nullable: false }),
			},
		},
		CourseInterest: {
			fields: {
				id: scalar(),
				course: hasOne('Course', { inverse: 'interests', nullable: false }),
				email: scalar(),
				firstName: scalar(),
				lastName: scalar(),
				note: scalar(),
				createdAt: scalar(),
				notifiedAt: scalar(),
			},
		},
		CourseTag: {
			fields: {
				id: scalar(),
				name: scalar(),
				slug: scalar(),
				color: scalar(),
				order: scalar(),
				courses: hasMany('Course', { inverse: 'tags', relationKind: 'manyHasMany' }),
			},
		},
		CourseTerm: {
			fields: {
				id: scalar(),
				course: hasOne('Course', { inverse: 'terms', nullable: false }),
				createdAt: scalar(),
				location: scalar(),
				mapEmbed: scalar(),
				priceKc: scalar(),
				capacity: scalar(),
				registeredCount: scalar(),
				status: enumScalar('CourseTermStatus', ['upcoming', 'cancelled', 'past']),
				heroImageEntity: hasOne('Image'),
				preCourseInfoEmailEnabled: scalar(),
				preCourseInfoInstructions: scalar(),
				firstDate: scalar(),
				dates: hasMany('CourseTermDate', { inverse: 'term', relationKind: 'oneHasMany', nullable: false }),
			},
		},
		CourseTermDate: {
			fields: {
				id: scalar(),
				term: hasOne('CourseTerm', { inverse: 'dates', nullable: false }),
				startsAt: scalar(),
				endsAt: scalar(),
				location: scalar(),
				mapEmbed: scalar(),
			},
		},
		DiscountCode: {
			fields: {
				id: scalar(),
				code: scalar(),
				percentOff: scalar(),
				maxUses: scalar(),
				usesCount: scalar(),
				validFrom: scalar(),
				validUntil: scalar(),
				appliesToCourses: hasMany('Course', { relationKind: 'manyHasMany' }),
				note: scalar(),
			},
		},
		Order: {
			fields: {
				id: scalar(),
				orderNumber: scalar(),
				status: enumScalar('OrderStatus', ['new', 'awaitingPayment', 'paid', 'substitute', 'cancelled']),
				isManual: scalar(),
				course: hasOne('Course', { nullable: false }),
				courseTerm: hasOne('CourseTerm'),
				contactName: scalar(),
				contactEmail: scalar(),
				contactPhone: scalar(),
				billingType: enumScalar('OrderBillingType', ['individual', 'company']),
				billingName: scalar(),
				billingIc: scalar(),
				billingDic: scalar(),
				billingStreet: scalar(),
				billingCity: scalar(),
				billingZip: scalar(),
				billingCountry: scalar(),
				organization: hasOne('Organization', { inverse: 'orders' }),
				pricePerParticipant: scalar(),
				participantsCount: scalar(),
				discountCode: hasOne('DiscountCode'),
				discountPercent: scalar(),
				totalKc: scalar(),
				note: scalar(),
				consentTermsAt: scalar(),
				consentNewsletter: scalar(),
				fapiInvoiceId: scalar(),
				fapiInvoiceUrl: scalar(),
				fapiPaymentUrl: scalar(),
				fapiDueDate: scalar(),
				fapiPaidInvoiceId: scalar(),
				fapiPaidInvoiceUrl: scalar(),
				fapiPaidAt: scalar(),
				fapiPaymentNotifiedAt: scalar(),
				fapiFinalInvoiceCheckAttempts: scalar(),
				fapiFinalInvoiceCheckRetryAt: scalar(),
				fapiFinalInvoiceProblemAt: scalar(),
				fapiFinalInvoiceProblem: scalar(),
				confirmationSentAt: scalar(),
				reminderSentAt: scalar(),
				finalInvoiceSentAt: scalar(),
				preCourseInfoSentAt: scalar(),
				participants: hasMany('Participant', { inverse: 'order', relationKind: 'oneHasMany', nullable: false }),
				emailLogs: hasMany('EmailLog', { inverse: 'order', relationKind: 'oneHasMany', nullable: false }),
				termChanges: hasMany('OrderTermChange', { inverse: 'order', relationKind: 'oneHasMany', nullable: false }),
				createdAt: scalar(),
			},
		},
		OrderTermChange: {
			fields: {
				id: scalar(),
				order: hasOne('Order', { inverse: 'termChanges', nullable: false }),
				fromTerm: hasOne('CourseTerm'),
				toTerm: hasOne('CourseTerm'),
				participants: hasMany('Participant', { relationKind: 'manyHasMany' }),
				participantsCount: scalar(),
				actorPersonId: scalar(),
				actorEmail: scalar(),
				createdAt: scalar(),
			},
		},
		EmailLog: {
			fields: {
				contactFormSubmission: hasOne('ContactFormSubmission', { inverse: 'emailLogs' }),
				id: scalar(),
				order: hasOne('Order', { inverse: 'emailLogs' }),
				gdOrder: hasOne('GdOrder'),
				gdUser: hasOne('GdUser'),
				gdDemoUser: hasOne('GdDemoUser'),
				templateKey: scalar(),
				type: enumScalar('EmailLogType', [
					'orderConfirmation',
					'substituteAck',
					'promotedFromSubstitute',
					'paymentReminder',
					'finalInvoice',
					'preCourseInfo',
					'gdMessage',
					'adminNotification',
					'contactForm',
					'courseInterest',
				]),
				status: enumScalar('EmailLogStatus', ['pending', 'sent', 'queued', 'rejected', 'invalid', 'failed']),
				subject: scalar(),
				fromEmail: scalar(),
				fromName: scalar(),
				replyTo: scalar(),
				recipientEmail: scalar(),
				recipientName: scalar(),
				recipientsJson: scalar(),
				tagsJson: scalar(),
				attachmentsJson: scalar(),
				providerMessageId: scalar(),
				providerStatus: scalar(),
				providerRejectReason: scalar(),
				errorMessage: scalar(),
				html: scalar(),
				text: scalar(),
				contentExpiresAt: scalar(),
				contentPurgedAt: scalar(),
				createdAt: scalar(),
				sentAt: scalar(),
			},
		},
		EmailText: {
			fields: {
				id: scalar(),
				key: scalar(),
				group: scalar(),
				label: scalar(),
				value: scalar(),
				variables: scalar(),
				order: scalar(),
			},
		},
		GdTargetGroup: {
			fields: {
				id: scalar(),
				name: scalar(),
				order: scalar(),
				legacyId: scalar(),
				calls: hasMany('GdCall', { inverse: 'targetGroups', relationKind: 'manyHasMany' }),
			},
		},
		GdFocusArea: {
			fields: {
				id: scalar(),
				name: scalar(),
				order: scalar(),
				legacyId: scalar(),
				calls: hasMany('GdCall', { inverse: 'focusAreas', relationKind: 'manyHasMany' }),
			},
		},
		GdRegion: {
			fields: {
				id: scalar(),
				name: scalar(),
				order: scalar(),
				legacyId: scalar(),
				calls: hasMany('GdCall', { inverse: 'regions', relationKind: 'manyHasMany' }),
			},
		},
		GdIssuerGroup: {
			fields: {
				id: scalar(),
				name: scalar(),
				order: scalar(),
				legacyId: scalar(),
				issuers: hasMany('GdIssuer', { inverse: 'group', relationKind: 'oneHasMany' }),
			},
		},
		GdPosition: {
			fields: {
				id: scalar(),
				name: scalar(),
				order: scalar(),
				legacyId: scalar(),
				contacts: hasMany('GdIssuerContact', { inverse: 'position', relationKind: 'oneHasMany' }),
			},
		},
		GdLegalForm: {
			fields: {
				id: scalar(),
				name: scalar(),
				code: scalar(),
				order: scalar(),
				issuers: hasMany('GdIssuer', { inverse: 'legalFormRef', relationKind: 'oneHasMany' }),
			},
		},
		GdIssuer: {
			fields: {
				id: scalar(),
				name: scalar(),
				ico: scalar(),
				dic: scalar(),
				icDphSk: scalar(),
				email: scalar(),
				phone: scalar(),
				web: scalar(),
				logo: scalar(),
				note: scalar(),
				group: hasOne('GdIssuerGroup', { inverse: 'issuers' }),
				legalFormRef: hasOne('GdLegalForm', { inverse: 'issuers' }),
				legalForm: scalar(),
				addresses: hasMany('GdIssuerAddress', { inverse: 'issuer', relationKind: 'oneHasMany', nullable: false }),
				defaultAddress: hasOne('GdIssuerAddress'),
				contacts: hasMany('GdIssuerContact', { inverse: 'issuer', relationKind: 'oneHasMany', nullable: false }),
				calls: hasMany('GdCall', { inverse: 'issuer', relationKind: 'oneHasMany' }),
				createdAt: scalar(),
				legacyCustomerId: scalar(),
			},
		},
		GdIssuerAddress: {
			fields: {
				id: scalar(),
				issuer: hasOne('GdIssuer', { inverse: 'addresses', nullable: false }),
				label: scalar(),
				street: scalar(),
				city: scalar(),
				zip: scalar(),
				country: scalar(),
				legacyId: scalar(),
				calls: hasMany('GdCall', { inverse: 'issuerAddress', relationKind: 'oneHasMany' }),
			},
		},
		GdIssuerContact: {
			fields: {
				id: scalar(),
				issuer: hasOne('GdIssuer', { inverse: 'contacts', nullable: false }),
				titles: scalar(),
				firstName: scalar(),
				lastName: scalar(),
				email: scalar(),
				phone: scalar(),
				position: hasOne('GdPosition', { inverse: 'contacts' }),
				isActive: scalar(),
				legacyId: scalar(),
				calls: hasMany('GdCall', { inverse: 'contacts', relationKind: 'manyHasMany' }),
			},
		},
		GdCall: {
			fields: {
				id: scalar(),
				title: scalar(),
				publicTitle: scalar(),
				status: enumScalar('GdCallStatus', ['draft', 'pendingApproval', 'published']),
				isPublic: scalar(),
				issuer: hasOne('GdIssuer', { inverse: 'calls' }),
				issuerAddress: hasOne('GdIssuerAddress', { inverse: 'calls' }),
				lastUpdatedAt: scalar(),
				perex: scalar(),
				description: scalar(),
				descriptionRich: scalar(),
				internalNote: scalar(),
				websiteUrl: scalar(),
				targetGroups: hasMany('GdTargetGroup', { inverse: 'calls', relationKind: 'manyHasMany' }),
				focusAreas: hasMany('GdFocusArea', { inverse: 'calls', relationKind: 'manyHasMany' }),
				regions: hasMany('GdRegion', { inverse: 'calls', relationKind: 'manyHasMany' }),
				contacts: hasMany('GdIssuerContact', { inverse: 'calls', relationKind: 'manyHasMany' }),
				maxDeadlineAt: scalar(),
				deadlines: hasMany('GdCallDeadline', { inverse: 'call', relationKind: 'oneHasMany', nullable: false }),
				seminars: hasMany('GdCallSeminar', { inverse: 'call', relationKind: 'oneHasMany', nullable: false }),
				notifications: hasMany('GdCallNotification', { inverse: 'call', relationKind: 'oneHasMany', nullable: false }),
				favorites: hasMany('GdFavoriteCall', { inverse: 'call', relationKind: 'oneHasMany', nullable: false }),
				favoriteCount: scalar(),
				publishedAt: scalar(),
				suppressNotification: scalar(),
				submittedIssuerText: scalar(),
				submittedContactText: scalar(),
				submittedSeminarText: scalar(),
				offersConsent: scalar(),
				offersEmail: scalar(),
				submissionNotificationDueAt: scalar(),
				createdAt: scalar(),
				legacyId: scalar(),
			},
		},
		GdCallDeadline: {
			fields: {
				id: scalar(),
				call: hasOne('GdCall', { inverse: 'deadlines', nullable: false }),
				date: scalar(),
				isRolling: scalar(),
				allocation: scalar(),
				grants: scalar(),
				info: scalar(),
				order: scalar(),
				legacyId: scalar(),
			},
		},
		GdCallNotification: {
			fields: {
				id: scalar(),
				call: hasOne('GdCall', { inverse: 'notifications', nullable: false }),
				notifyAt: scalar(),
				notifiedAt: scalar(),
				order: scalar(),
				createdAt: scalar(),
			},
		},
		GdCallSeminar: {
			fields: {
				id: scalar(),
				call: hasOne('GdCall', { inverse: 'seminars', nullable: false }),
				title: scalar(),
				dateText: scalar(),
				place: scalar(),
				info: scalar(),
				contact: scalar(),
				order: scalar(),
				legacyId: scalar(),
			},
		},
		GdSubscriber: {
			fields: {
				id: scalar(),
				name: scalar(),
				isPerson: scalar(),
				ico: scalar(),
				dic: scalar(),
				icDphSk: scalar(),
				email: scalar(),
				phone: scalar(),
				web: scalar(),
				note: scalar(),
				billingStreet: scalar(),
				billingCity: scalar(),
				billingZip: scalar(),
				billingCountry: scalar(),
				dueDays: scalar(),
				standingDiscountPercent: scalar(),
				autoRenewDisabled: scalar(),
				autoRenewDisabledAt: scalar(),
				memberships: hasMany('GdMembership', { inverse: 'subscriber', relationKind: 'oneHasMany', nullable: false }),
				orders: hasMany('GdOrder', { inverse: 'subscriber', relationKind: 'oneHasMany', nullable: false }),
				createdAt: scalar(),
				legacyCustomerId: scalar(),
			},
		},
		GdMembership: {
			fields: {
				id: scalar(),
				user: hasOne('GdUser', { inverse: 'memberships', nullable: false }),
				subscriber: hasOne('GdSubscriber', { inverse: 'memberships', nullable: false }),
				isBillingContact: scalar(),
				isActive: scalar(),
				createdAt: scalar(),
			},
		},
		GdUser: {
			fields: {
				id: scalar(),
				email: scalar(),
				titles: scalar(),
				firstName: scalar(),
				lastName: scalar(),
				salutation: scalar(),
				phone: scalar(),
				organizationText: scalar(),
				isActive: scalar(),
				consentNewsletter: scalar(),
				consentNewsletterAt: scalar(),
				consentAge16: scalar(),
				consentAge16At: scalar(),
				consentMarketing: scalar(),
				consentMarketingAt: scalar(),
				registeredAt: scalar(),
				firstLoginAt: scalar(),
				lastLoginAt: scalar(),
				lastDigestAt: scalar(),
				notificationsEnabled: scalar(),
				digestIntervalDays: scalar(),
				filterTargetGroups: hasMany('GdTargetGroup', { relationKind: 'manyHasMany' }),
				filterFocusAreas: hasMany('GdFocusArea', { relationKind: 'manyHasMany' }),
				filterRegions: hasMany('GdRegion', { relationKind: 'manyHasMany' }),
				filterIssuerGroups: hasMany('GdIssuerGroup', { relationKind: 'manyHasMany' }),
				favoriteCalls: hasMany('GdFavoriteCall', { inverse: 'user', relationKind: 'oneHasMany', nullable: false }),
				favoriteIssuers: hasMany('GdIssuer', { relationKind: 'manyHasMany' }),
				memberships: hasMany('GdMembership', { inverse: 'user', relationKind: 'oneHasMany', nullable: false }),
				legacyUserId: scalar(),
			},
		},
		GdFavoriteCall: {
			fields: {
				id: scalar(),
				user: hasOne('GdUser', { inverse: 'favoriteCalls', nullable: false }),
				call: hasOne('GdCall', { inverse: 'favorites', nullable: false }),
				createdAt: scalar(),
			},
		},
		GdChrome: {
			fields: {
				id: scalar(),
				unique: enumScalar('GdChromeUnique', ['unique']),
				links: hasMany('GdChromeLink', { inverse: 'chrome', relationKind: 'oneHasMany' }),
				demoButtonLabel: scalar(),
				orderButtonLabel: scalar(),
				logoutLabel: scalar(),
				demoBadgeLabel: scalar(),
				callsLabel: scalar(),
				publicCallsLabel: scalar(),
				footerBrand: scalar(),
				footerClaim: scalar(),
				footerContactHeading: scalar(),
				footerLinksHeading: scalar(),
				legalPrivacyLabel: scalar(),
				legalCookiesLabel: scalar(),
				legalSnLabel: scalar(),
			},
		},
		GdChromeLink: {
			fields: {
				id: scalar(),
				chrome: hasOne('GdChrome', { inverse: 'links', nullable: false }),
				placement: enumScalar('GdChromeLinkPlacement', ['topbar', 'menu', 'menuUser', 'footer']),
				order: scalar(),
				label: scalar(),
				href: scalar(),
			},
		},
		GdPage: {
			fields: {
				id: scalar(),
				title: scalar(),
				path: scalar(),
				seoTitle: scalar(),
				seoDescription: scalar(),
				blocks: hasOne('BlockList'),
				publishedAt: scalar(),
			},
		},
		GdTestimonial: {
			fields: {
				id: scalar(),
				quote: scalar(),
				author: scalar(),
				role: scalar(),
				photo: hasOne('Image'),
				isPublished: scalar(),
				order: scalar(),
				createdAt: scalar(),
			},
		},
		GdPlan: {
			fields: {
				id: scalar(),
				name: scalar(),
				months: scalar(),
				price: scalar(),
				priceVat: scalar(),
				vatRate: scalar(),
				isActive: scalar(),
				order: scalar(),
				legacyId: scalar(),
				orders: hasMany('GdOrder', { inverse: 'plan', relationKind: 'oneHasMany' }),
			},
		},
		GdDiscountCode: {
			fields: {
				id: scalar(),
				code: scalar(),
				percentOff: scalar(),
				maxUses: scalar(),
				usesCount: scalar(),
				validFrom: scalar(),
				validUntil: scalar(),
				plan: hasOne('GdPlan'),
				subscriber: hasOne('GdSubscriber'),
				origin: enumScalar('GdDiscountCodeOrigin', ['manual', 'reactivation']),
				note: scalar(),
				createdAt: scalar(),
				orders: hasMany('GdOrder', { inverse: 'discountCode', relationKind: 'oneHasMany' }),
			},
		},
		GdOrder: {
			fields: {
				id: scalar(),
				orderNumber: scalar(),
				status: enumScalar('GdOrderStatus', ['new', 'awaitingPayment', 'paid', 'cancelled', 'expired']),
				subscriber: hasOne('GdSubscriber', { inverse: 'orders', nullable: false }),
				orderedByUser: hasOne('GdUser'),
				plan: hasOne('GdPlan', { inverse: 'orders' }),
				price: scalar(),
				priceVat: scalar(),
				vatRate: scalar(),
				discountCode: hasOne('GdDiscountCode', { inverse: 'orders' }),
				appliedDiscountPercent: scalar(),
				periodStart: scalar(),
				periodEnd: scalar(),
				renewedFromOrder: hasOne('GdOrder', { inverse: 'renewals' }),
				renewals: hasMany('GdOrder', { inverse: 'renewedFromOrder', relationKind: 'oneHasMany' }),
				fapiProformaId: scalar(),
				fapiProformaUrl: scalar(),
				fapiPaymentUrl: scalar(),
				fapiInvoiceId: scalar(),
				dueDate: scalar(),
				confirmationSentAt: scalar(),
				confirmationClaimedAt: scalar(),
				welcomeSentAt: scalar(),
				welcomeClaimedAt: scalar(),
				fapiProformaRequestedAt: scalar(),
				fapiProformaAttempts: scalar(),
				fapiProformaProblemAt: scalar(),
				fapiProformaProblem: scalar(),
				fapiPaymentCheckedAt: scalar(),
				paymentSettledVia: scalar(),
				processingAttempts: scalar(),
				processingRetryAt: scalar(),
				billingName: scalar(),
				billingIc: scalar(),
				billingDic: scalar(),
				billingStreet: scalar(),
				billingCity: scalar(),
				billingZip: scalar(),
				billingCountry: scalar(),
				billingEmail: scalar(),
				isManual: scalar(),
				paidOutsideFapi: scalar(),
				createdAt: scalar(),
				paidAt: scalar(),
				legacyId: scalar(),
			},
		},
		GdCheckoutProbe: {
			fields: {
				at: scalar(),
				ico: scalar(),
				outcome: scalar(),
				ip: scalar(),
			},
		},
		GdTask: {
			fields: {
				id: scalar(),
				type: enumScalar('GdTaskType', [
					'phoneNewOrderDue',
					'phoneNoLogin',
					'phoneRenewalDue',
					'phoneFarewell',
					'phoneReactivation',
					'contactChangeReview',
					'other',
				]),
				subscriber: hasOne('GdSubscriber'),
				user: hasOne('GdUser'),
				order: hasOne('GdOrder'),
				reason: scalar(),
				dueAt: scalar(),
				doneAt: scalar(),
				doneBy: hasOne('User'),
				note: scalar(),
				createdAt: scalar(),
			},
		},
		GdDemoUser: {
			fields: {
				id: scalar(),
				firstName: scalar(),
				lastName: scalar(),
				phone: scalar(),
				email: scalar(),
				organization: scalar(),
				emailConfirmedAt: scalar(),
				firstAccessAt: scalar(),
				lastAccessAt: scalar(),
				accessCount: scalar(),
				mailchimpSyncedAt: scalar(),
				convertedToSubscriber: hasOne('GdSubscriber'),
				createdAt: scalar(),
				legacyUserId: scalar(),
			},
		},
		GdSettings: {
			fields: {
				id: scalar(),
				unique: enumScalar('GdSettingsUnique', ['unique']),
				publicCallsEnabled: scalar(),
				adminNotificationEmail: scalar(),
				callSubmissionNotificationEmail: scalar(),
				digestSendHour: scalar(),
				deadlineReminderDays: scalar(),
				renewalGenerationHour: scalar(),
				renewalLeadDays: scalar(),
				dueDaysDefault: scalar(),
				paymentReminderDays: scalar(),
				paymentReminderCallDays: scalar(),
				loginReminderDays: scalar(),
				loginReminderCallDays: scalar(),
				usageCheckMonths: scalar(),
				renewalReminderDays: scalar(),
				renewalReminderCallDays: scalar(),
				reactivationOfferDays: scalar(),
				reactivationReminderDays: scalar(),
				reactivationCallDays: scalar(),
				reactivationDiscountPercent: scalar(),
				reactivationDiscountValidDays: scalar(),
				demoAccessReminderDays: scalar(),
				demoFirstImpressionsDays: scalar(),
			},
		},
		Organization: {
			fields: {
				id: scalar(),
				ic: scalar(),
				dic: scalar(),
				name: scalar(),
				street: scalar(),
				city: scalar(),
				zip: scalar(),
				country: scalar(),
				note: scalar(),
				orders: hasMany('Order', { inverse: 'organization', relationKind: 'oneHasMany' }),
				firstOrderAt: scalar(),
				lastOrderAt: scalar(),
			},
		},
		Participant: {
			fields: {
				id: scalar(),
				order: hasOne('Order', { inverse: 'participants', nullable: false }),
				courseTerm: hasOne('CourseTerm'),
				firstName: scalar(),
				preCourseInfoSentAt: scalar(),
				lastName: scalar(),
				email: scalar(),
				phone: scalar(),
				isContactPerson: scalar(),
				attended: scalar(),
			},
		},
		User: {
			fields: {
				id: scalar(),
				personId: scalar(),
				email: scalar(),
				firstName: scalar(),
				lastName: scalar(),
				createdAt: scalar(),
			},
		},
	},
})

export const imageEntity = entityDef<Image>('Image', adminSchema)
export const urlEntity = entityDef<Url>('Url', adminSchema)
export const linkEntity = entityDef<Link>('Link', adminSchema)
export const seoMetaEntity = entityDef<SeoMeta>('SeoMeta', adminSchema)
export const contentEntity = entityDef<Content>('Content', adminSchema)
export const contentReferenceEntity = entityDef<ContentReference>('ContentReference', adminSchema)
export const contentGalleryItemEntity = entityDef<ContentGalleryItem>('ContentGalleryItem', adminSchema)
export const redirectEntity = entityDef<Redirect>('Redirect', adminSchema)
export const bannerEntity = entityDef<Banner>('Banner', adminSchema)

export const articleEntity = entityDef<Article>('Article', adminSchema)
export const personEntity = entityDef<Person>('Person', adminSchema)
export const podcastEntity = entityDef<Podcast>('Podcast', adminSchema)
export const tagEntity = entityDef<Tag>('Tag', adminSchema)

export const pageEntity = entityDef<Page>('Page', adminSchema)
export const blockListEntity = entityDef<BlockList>('BlockList', adminSchema)
export const blockEntity = entityDef<Block>('Block', adminSchema)
export const blockItemEntity = entityDef<BlockItem>('BlockItem', adminSchema)
export const blockArticleEntity = entityDef<BlockArticle>('BlockArticle', adminSchema)
export const blockCourseEntity = entityDef<BlockCourse>('BlockCourse', adminSchema)
export const blockPersonEntity = entityDef<BlockPerson>('BlockPerson', adminSchema)
export const navigationEntity = entityDef<Navigation>('Navigation', adminSchema)
export const navigationItemEntity = entityDef<NavigationItem>('NavigationItem', adminSchema)
export const footerEntity = entityDef<Footer>('Footer', adminSchema)
export const footerColumnEntity = entityDef<FooterColumn>('FooterColumn', adminSchema)
export const footerLinkEntity = entityDef<FooterLink>('FooterLink', adminSchema)
export const supportCtaEntity = entityDef<SupportCta>('SupportCta', adminSchema)
export const siteSettingsEntity = entityDef<SiteSettings>('SiteSettings', adminSchema)

export const courseEntity = entityDef<Course>('Course', adminSchema)
export const courseInterestEntity = entityDef<CourseInterest>('CourseInterest', adminSchema)
export const contactFormEntity = entityDef<ContactForm>('ContactForm', adminSchema)
export const contactFormFieldEntity = entityDef<ContactFormField>('ContactFormField', adminSchema)
export const contactFormSubmissionEntity = entityDef<ContactFormSubmission>('ContactFormSubmission', adminSchema)
export const courseTagEntity = entityDef<CourseTag>('CourseTag', adminSchema)
export const courseTermEntity = entityDef<CourseTerm>('CourseTerm', adminSchema)
export const courseTermDateEntity = entityDef<CourseTermDate>('CourseTermDate', adminSchema)
export const discountCodeEntity = entityDef<DiscountCode>('DiscountCode', adminSchema)
export const emailLogEntity = entityDef<EmailLog>('EmailLog', adminSchema)
export const emailTextEntity = entityDef<EmailText>('EmailText', adminSchema)
export const orderEntity = entityDef<Order>('Order', adminSchema)
export const orderTermChangeEntity = entityDef<OrderTermChange>('OrderTermChange', adminSchema)
export const organizationEntity = entityDef<Organization>('Organization', adminSchema)
export const participantEntity = entityDef<Participant>('Participant', adminSchema)

export const gdCallEntity = entityDef<GdCall>('GdCall', adminSchema)
export const gdCallDeadlineEntity = entityDef<GdCallDeadline>('GdCallDeadline', adminSchema)
export const gdCallNotificationEntity = entityDef<GdCallNotification>('GdCallNotification', adminSchema)
export const gdCallSeminarEntity = entityDef<GdCallSeminar>('GdCallSeminar', adminSchema)
export const gdDemoUserEntity = entityDef<GdDemoUser>('GdDemoUser', adminSchema)
export const gdDiscountCodeEntity = entityDef<GdDiscountCode>('GdDiscountCode', adminSchema)
export const gdFavoriteCallEntity = entityDef<GdFavoriteCall>('GdFavoriteCall', adminSchema)
export const gdFocusAreaEntity = entityDef<GdFocusArea>('GdFocusArea', adminSchema)
export const gdIssuerEntity = entityDef<GdIssuer>('GdIssuer', adminSchema)
export const gdIssuerAddressEntity = entityDef<GdIssuerAddress>('GdIssuerAddress', adminSchema)
export const gdIssuerContactEntity = entityDef<GdIssuerContact>('GdIssuerContact', adminSchema)
export const gdIssuerGroupEntity = entityDef<GdIssuerGroup>('GdIssuerGroup', adminSchema)
export const gdCheckoutProbeEntity = entityDef<GdCheckoutProbe>('GdCheckoutProbe', adminSchema)
export const gdLegalFormEntity = entityDef<GdLegalForm>('GdLegalForm', adminSchema)
export const gdMembershipEntity = entityDef<GdMembership>('GdMembership', adminSchema)
export const gdOrderEntity = entityDef<GdOrder>('GdOrder', adminSchema)
export const gdPlanEntity = entityDef<GdPlan>('GdPlan', adminSchema)
export const gdPositionEntity = entityDef<GdPosition>('GdPosition', adminSchema)
export const gdRegionEntity = entityDef<GdRegion>('GdRegion', adminSchema)
export const gdSettingsEntity = entityDef<GdSettings>('GdSettings', adminSchema)
export const gdSubscriberEntity = entityDef<GdSubscriber>('GdSubscriber', adminSchema)
export const gdTargetGroupEntity = entityDef<GdTargetGroup>('GdTargetGroup', adminSchema)
export const gdTaskEntity = entityDef<GdTask>('GdTask', adminSchema)
export const gdTestimonialEntity = entityDef<GdTestimonial>('GdTestimonial', adminSchema)
export const gdChromeEntity = entityDef<GdChrome>('GdChrome', adminSchema)
export const gdChromeLinkEntity = entityDef<GdChromeLink>('GdChromeLink', adminSchema)
export const gdPageEntity = entityDef<GdPage>('GdPage', adminSchema)
export const gdUserEntity = entityDef<GdUser>('GdUser', adminSchema)
